import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import {
  formatLocaleCurrency,
  formatLocaleDate,
  formatLocaleNettoPoints,
  formatLocalePercentageNumber,
} from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Col, Descriptions, Divider, Row, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useDownloadContractCommissionsExport, useFilterContractCommissions } from "../../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../../common/constants";
import { ExportFileType } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import { EntityIdObject } from "../../../../../common/types";
import { formatAgentName } from "../../../../agent/utils";
import { useHasPermissions } from "../../../../auth/hooks";
import { CommissionKindTag } from "../../../../commissions/batch/components/CommissionKindTag";
import { CommissionBatchName } from "../../../../commissions/components/CommissionBatchName";
import { CommissionShortcut } from "../../../../commissions/enums";
import { ContractCommission } from "../../../types";

export const ContractCommissionTableView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { id } = useParams<EntityIdObject>() as { id: string };

  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);

  const searchParams = useFilterSearchParams({ pageSize: PageSizes.SMALL });

  const { data, isFetching } = useFilterContractCommissions({
    id,
    object: { pageIndex: searchParams.pageIndex, pageSize: searchParams.pageSize },
  });

  const downloadContractCommissionsExport = useDownloadContractCommissionsExport();

  const columns = useMemo(() => {
    const items: ColumnsType<ContractCommission> = [
      {
        key: "batch",
        title: t("commissions.batch.attrs.commission.batch"),
        width: 160,
        ellipsis: { showTitle: false },
        render: (_, record) => (
          <CommissionBatchName
            batchName={record.batch.name}
            batchId={record.batch.id}
            showLink={hasCommissionsPermission}
          />
        ),
      },
      {
        key: "tariffCode",
        title: t("commissions.batch.attrs.commission.tariffCode"),
        width: 90,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.tariffCode}</Ellipsis>,
      },
      {
        key: "dates",
        title: t("commissions.batch.attrs.commission.dates"),
        width: 95,
        render: (_, record) => (
          <>
            {record.startDate ? formatLocaleDate(record.startDate) : undefined}
            <br />
            {record.endDate ? formatLocaleDate(record.endDate) : undefined}
          </>
        ),
      },
      {
        key: "commissionKind",
        title: t("commissions.batch.enums.commissionKind._label"),
        align: "center",
        width: 190,
        render: (_, record) => (
          <>
            {record.commissionKind ? <CommissionKindTag kind={record.commissionKind} /> : undefined}
            {record.splitOriginalCommission ? (
              <div className="sub-header-info">{`${t("commissions.splitCommissions.helpers.splitLabelShort", {
                year: record.splitOriginalCommission.paymentYear,
              })} (${formatLocalePercentageNumber(record.splitOriginalCommission.paymentRatio)})`}</div>
            ) : undefined}
          </>
        ),
      },
      {
        key: "commissionBaseAmount",
        title: t("commissions.batch.attrs.commission.commissionBaseAmount"),
        align: "right",
        width: 130,
        render: (_, record) =>
          isDefined(record.commissionBaseAmount) ? formatLocaleCurrency(record.commissionBaseAmount) : undefined,
      },
    ];

    if (hasCommissionsPermission) {
      items.push({
        key: "commissionAmount",
        title: t("commissions.batch.attrs.commission.commissionAmount"),
        align: "right",
        width: 120,
        render: (_, record) =>
          isDefined(record.splitPayoutCommissionAmount) ? (
            <div>
              <div>{formatLocaleCurrency(record.splitPayoutCommissionAmount)}</div>
              <div className="sub-header-info">
                {isDefined(record.commissionAmount) ? formatLocaleCurrency(record.commissionAmount) : undefined}
              </div>
            </div>
          ) : (
            <span>
              {isDefined(record.commissionAmount) ? formatLocaleCurrency(record.commissionAmount) : undefined}
            </span>
          ),
      });
    }

    items.push({
      key: "calculations",
      title: (
        <LabelWithPopover
          popoverContent={
            <Descriptions
              items={Object.values(CommissionShortcut).map((item) => ({
                label: item,
                children: t(`commissions.enums.commissionShortcut.${item}`),
              }))}
              style={{ width: 230 }}
              bordered
              size="small"
              column={1}
            />
          }
          popoverTitle={t("commissions.enums.commissionShortcut._label")}
          label={t("commissions.batch.helpers.commissionsDivision")}
        />
      ),
      align: "right",
      fixed: "right",
      width: hasCommissionsPermission ? 365 : 485,
      render: (_, record) =>
        record.calculatedCommissions.map((cc, index) => (
          <React.Fragment key={cc.id}>
            <Row>
              <Col span={11}>
                <Ellipsis>{formatAgentName(cc.gainer)}</Ellipsis>
              </Col>
              <Col span={5} className="right-align">
                {isDefined(cc.commissionAmount) ? formatLocaleCurrency(cc.commissionAmount) : undefined}
              </Col>
              <Col span={5} className="right-align">
                {isDefined(cc.nettoPointAmount) ? formatLocaleNettoPoints(cc.nettoPointAmount) : undefined}
              </Col>
              <Col span={3} className="center-align">
                {cc.commissionShortcut}
              </Col>
            </Row>

            {cc.individualNettoPoints && index < record.calculatedCommissions.length - 1 && (
              <Divider style={{ fontSize: 14, marginTop: 5, marginBottom: 5 }} />
            )}
          </React.Fragment>
        )),
    });

    return items;
  }, [hasCommissionsPermission, t]);

  return (
    <Table<ContractCommission>
      {...tableStandardProps()}
      columns={columns}
      title={(data) => {
        if (!data.length) return;

        return (
          <Space>
            <ActionTextIcon
              icon="download"
              color="blue"
              text={t("common.exportXlsx")}
              onClick={() =>
                downloadContractCommissionsExport.mutate({ id, object: { exportFileType: ExportFileType.XLSX } })
              }
            />

            <ActionTextIcon
              icon="download"
              color="green"
              text={t("common.exportCsv")}
              onClick={() =>
                downloadContractCommissionsExport.mutate({ id, object: { exportFileType: ExportFileType.CSV } })
              }
            />
          </Space>
        );
      }}
      dataSource={data?.pageData}
      loading={isFetching}
      scroll={{ x: TableSizes.LARGE }}
      pagination={{
        ...paginationTableProps,
        style: { marginBottom: 0 },
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
